import { ID, NewTask, TaskType } from '@griply/types';

import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';
import { buildTaskDocument } from './build-task-document';

export type CreateSubTaskOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TTimestamp = unknown,
  TDb = FirestoreInstance,
> = {
  name: string;
  parentId: ID;
  parentParentIds: ID[] | null;
  parentChildIds: ID[] | null;
  // Pre-generated sub-task id. When supplied, the caller already knows the id
  // (e.g. for optimistic rendering) and the document is created at that id.
  id?: ID;
  userId: ID;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (
    collectionRef: NoInfer<TCollectionRef>,
    ...rest: string[]
  ) => TDocRef & { id: ID };
  db: TDb;
  serverTimestamp: () => TFieldValue;
  timestampFromDate: (date: Date) => TTimestamp;
  // Atomic array append for the parent's childIds. When supplied, concurrent
  // sub-task creates each add their own id without read-modify-writing a stale
  // snapshot (which clobbers siblings). Falls back to parentChildIds spread
  // when absent, so existing callers are unaffected.
  arrayUnion?: (...ids: ID[]) => TFieldValue;
  // When false, only the sub-task document is written — the parent's childIds
  // is NOT touched. Lets a caller avoid serialising every create on the single
  // parent document (write contention) and persist childIds order separately /
  // debounced. Defaults to true so existing callers are unaffected.
  updateParentChildIds?: boolean;
  writeBatch: (db: NoInfer<TDb>) => {
    set: (ref: NoInfer<TDocRef>, data: Record<string, unknown>) => void;
    update: (ref: NoInfer<TDocRef>, data: Record<string, unknown>) => void;
    commit: () => Promise<void>;
  };
  onWriteError: (error: unknown) => void;
};

export const createSubTask = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TTimestamp,
  TDb = FirestoreInstance,
>(
  options: CreateSubTaskOptions<
    TDocRef,
    TCollectionRef,
    TFieldValue,
    TTimestamp,
    TDb
  >,
): Promise<{ id: ID; parentIds: ID[]; name: string } | null> => {
  const {
    name,
    parentId,
    parentParentIds,
    parentChildIds,
    id,
    userId,
    collection,
    doc,
    db,
    serverTimestamp,
    timestampFromDate,
    arrayUnion,
    updateParentChildIds,
    writeBatch,
    onWriteError,
  } = options;

  const trimmedName = name.trim();
  if (!trimmedName) {
    return null;
  }

  const parentIds = [...(parentParentIds ?? []), parentId];

  const taskCollection = collection(db, FirestoreCollections.Tasks);
  const subTaskRef = id ? doc(taskCollection, id) : doc(taskCollection);
  const subTaskDoc = buildTaskDocument(
    { name: trimmedName, type: TaskType.Task } as NewTask,
    subTaskRef,
    userId,
    serverTimestamp,
    timestampFromDate,
    parentIds,
  );

  const batch = writeBatch(db);
  batch.set(subTaskRef, subTaskDoc);
  if (updateParentChildIds !== false) {
    const parentRef = doc(taskCollection, parentId);
    batch.update(parentRef, {
      childIds: arrayUnion
        ? arrayUnion(subTaskRef.id)
        : [...(parentChildIds ?? []), subTaskRef.id],
      updatedAt: serverTimestamp(),
    });
  }
  detachWrite(batch.commit(), onWriteError);

  return { id: subTaskRef.id, parentIds, name: trimmedName };
};
